import React from 'react';
import { cn } from '@crisellevaldez/speculo-ui';
import {
  User,
  Globe,
  ArrowUpRight,
  ArrowDownRight,
  MapPin,
  Sparkles,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  DollarSign,
  TrendingUp,
  Calendar,
  Building2,
  School,
  Receipt,
  Banknote,
  ScrollText,
} from 'lucide-react';
import type { Contact } from '../../../../types/contact.types';
import type { GQLPropertyEnrichmentItem } from '../../../../hooks/graphql/contacts/useContact';
import { formatCurrency } from '../../../../utils/formatters';
import { LiveIndicator } from '../../../common/LiveIndicator';
import { Tooltip } from '../../../common/Tooltip';
import { IconPill } from '../../../common/IconPill';
import { buildHomeownerSummary } from './buildHomeownerSummary';

interface HomeownerDataPanelProps {
  contact: Contact | null;
  enrichmentItem?: GQLPropertyEnrichmentItem | null;
}

const boolIcon = (v: boolean | null): React.ReactNode | null => {
  if (v === null) return null;
  return v ? (
    <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400 inline-block" />
  ) : (
    <XCircle className="w-4 h-4 text-muted-foreground inline-block" />
  );
};

const boolIconWithArea = (v: boolean | null, areaSf: number | null): React.ReactNode | null => {
  if (v === null) return null;
  return (
    <span className="inline-flex items-center gap-1.5 justify-end">
      {boolIcon(v)}
      {v && areaSf != null && areaSf > 0 && (
        <span className="text-xs text-muted-foreground">{areaSf.toLocaleString()} sf</span>
      )}
    </span>
  );
};

type PillTone = 'green' | 'amber' | 'red' | 'blue' | 'gray' | 'purple';

const PILL_TONES: Record<PillTone, string> = {
  green:
    'bg-green-50 text-green-700 ring-green-200 dark:bg-green-500/15 dark:text-green-300 dark:ring-green-500/30',
  amber:
    'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-amber-500/30',
  red:
    'bg-red-50 text-red-700 ring-red-200 dark:bg-red-500/15 dark:text-red-300 dark:ring-red-500/30',
  blue:
    'bg-blue-50 text-blue-700 ring-blue-200 dark:bg-blue-500/15 dark:text-blue-300 dark:ring-blue-500/30',
  gray:
    'bg-muted/40 text-foreground/70 ring-foreground/15 dark:ring-foreground/20',
  purple:
    'bg-purple-50 text-purple-700 ring-purple-200 dark:bg-purple-500/15 dark:text-purple-300 dark:ring-purple-500/30',
};

export const HomeownerDataPanel: React.FC<HomeownerDataPanelProps> = ({ contact, enrichmentItem }) => {
  if (!contact) return null;
  const data = buildHomeownerSummary(contact, enrichmentItem);

  // Status pills — high-signal flags surfaced above the detail blocks so the user can
  // scan the situation at a glance instead of hunting through label/value rows.
  type PillEntry = { tone: PillTone; label: string; icon?: React.ReactNode };
  const pills: PillEntry[] = [];
  if (data.ownerOccupied === true) {
    pills.push({ tone: 'green', label: 'Owner-occupied', icon: <CheckCircle2 className="w-3 h-3" /> });
  } else if (data.ownerOccupied === false) {
    pills.push({ tone: 'gray', label: 'Not owner-occupied' });
  }
  if (data.vacant === true) {
    pills.push({ tone: 'amber', label: 'Vacant', icon: <XCircle className="w-3 h-3" /> });
  }
  if (data.corporateOwned === true) pills.push({ tone: 'gray', label: 'Corporate-owned' });
  if (data.absenteeOwner === true) {
    const where = data.outOfStateAbsenteeOwner
      ? 'Absentee · out-of-state'
      : data.inStateAbsenteeOwner
      ? 'Absentee · in-state'
      : 'Absentee owner';
    pills.push({ tone: 'gray', label: where });
  }
  if (data.cashBuyer === true) pills.push({ tone: 'blue', label: 'Cash buyer' });
  if (data.investorBuyer === true) pills.push({ tone: 'blue', label: 'Investor buyer' });
  if (data.highEquity === true) pills.push({ tone: 'green', label: 'High equity' });
  if (data.freeClear === true) pills.push({ tone: 'green', label: 'Free & clear' });
  if (data.negativeEquity === true) pills.push({ tone: 'red', label: 'Negative equity' });
  if (data.inherited === true) pills.push({ tone: 'gray', label: 'Inherited' });
  if (data.death === true) pills.push({ tone: 'gray', label: 'Death on record' });
  if (data.mlsActive === true || data.forSale === true) {
    pills.push({ tone: 'purple', label: 'Listed for sale' });
  }
  if (data.priceReduced === true) pills.push({ tone: 'purple', label: 'Price reduced' });
  if (data.preForeclosure === true) {
    pills.push({ tone: 'red', label: 'Pre-foreclosure', icon: <AlertTriangle className="w-3 h-3" /> });
  }
  if (data.reo === true) pills.push({ tone: 'red', label: 'REO', icon: <AlertTriangle className="w-3 h-3" /> });
  if (data.auction === true) {
    pills.push({ tone: 'red', label: 'Auction', icon: <AlertTriangle className="w-3 h-3" /> });
  }
  if (data.taxLien === true) {
    pills.push({ tone: 'red', label: 'Tax lien', icon: <AlertTriangle className="w-3 h-3" /> });
  }
  if (data.judgment === true) {
    pills.push({ tone: 'red', label: 'Judgment', icon: <AlertTriangle className="w-3 h-3" /> });
  }

  // KPI cards — colour each card by what its value MEANS for outreach:
  //   green = positive seller signal, amber = mid, red = distress/concern, gray = neutral fact.
  type KpiEntry = { label: string; value: string; sub?: string | null; tone?: PillTone; icon: React.ReactNode };
  const kpis: KpiEntry[] = [];
  if (data.equityPercent !== null) {
    // ≥50% = strong seller (lots of room to discount); <10% = thin cushion / underwater risk.
    kpis.push({
      label: 'Equity',
      value: `${data.equityPercent}%`,
      sub: data.estimatedEquity ?? null,
      tone: data.equityPercent >= 50 ? 'green' : data.equityPercent < 10 ? 'red' : 'amber',
      icon: <TrendingUp className="w-3.5 h-3.5" />,
    });
  }
  const yearsOwnedNum = data.yearsOwnedServer ?? data.yearsOwned;
  if (yearsOwnedNum !== null) {
    // ≥8 = past the typical hold period, ripe to sell; 3–7 = mid-tenure; <3 = recent buyer.
    kpis.push({
      label: 'Years owned',
      value: `${yearsOwnedNum}`,
      sub: data.lastSaleDate ? new Date(data.lastSaleDate).toLocaleDateString() : null,
      tone: yearsOwnedNum >= 8 ? 'green' : yearsOwnedNum >= 3 ? 'amber' : 'gray',
      icon: <Calendar className="w-3.5 h-3.5" />,
    });
  }
  if (data.annualTaxes) {
    kpis.push({
      label: 'Annual taxes',
      value: data.annualTaxes,
      sub: data.taxYear != null ? String(data.taxYear) : null,
      tone: 'gray',
      icon: <Receipt className="w-3.5 h-3.5" />,
    });
  } else if (data.taxTotal) {
    kpis.push({
      label: 'Tax assessed',
      value: data.taxTotal,
      sub: data.taxYear != null ? String(data.taxYear) : null,
      tone: 'gray',
      icon: <Receipt className="w-3.5 h-3.5" />,
    });
  }
  if (data.mlsDaysOnMarket !== null) {
    // <30 = fresh listing; 30–180 = stale; >180 = very stale → seller likely motivated.
    kpis.push({
      label: 'Days on market',
      value: String(data.mlsDaysOnMarket),
      sub: data.mlsStatus ?? null,
      tone:
        data.mlsDaysOnMarket > 180
          ? 'red'
          : data.mlsDaysOnMarket > 30
          ? 'amber'
          : 'green',
      icon: <TrendingUp className="w-3.5 h-3.5" />,
    });
  } else if (data.mlsStatus) {
    // Status string → tone. Active/Pending = live activity, Cancelled/Failed = motivated.
    const s = data.mlsStatus.toLowerCase();
    const mlsTone: PillTone = s.includes('active')
      ? 'purple'
      : s.includes('pending')
      ? 'blue'
      : s.includes('cancel') || s.includes('fail') || s.includes('expired')
      ? 'red'
      : 'gray';
    kpis.push({
      label: 'MLS status',
      value: data.mlsStatus,
      sub: data.mlsLastStatusDate ?? null,
      tone: mlsTone,
      icon: <TrendingUp className="w-3.5 h-3.5" />,
    });
  }
  if (kpis.length < 4 && data.openMortgageBalance) {
    // openMortgagePercent is a value-share — high % = lots still owed = less equity room.
    const pct = data.openMortgagePercent;
    const mortTone: PillTone =
      pct == null ? 'gray' : pct < 20 ? 'green' : pct > 80 ? 'red' : 'amber';
    kpis.push({
      label: 'Mortgage balance',
      value: data.openMortgageBalance,
      sub: data.lenderName ?? (pct != null ? `${pct}% of value` : null),
      tone: mortTone,
      icon: <Banknote className="w-3.5 h-3.5" />,
    });
  }
  if (kpis.length < 4 && data.estimatedMortgagePayment) {
    kpis.push({
      label: 'Est. payment',
      value: data.estimatedMortgagePayment,
      sub: 'monthly',
      tone: 'gray',
      icon: <DollarSign className="w-3.5 h-3.5" />,
    });
  }

  const ownerName =
    data.ownerName1 ?? (`${contact.firstName} ${contact.lastName}`.trim() || null);
  const yearsOwnedDisplay =
    data.yearsOwnedServer != null
      ? `${data.yearsOwnedServer} years`
      : data.yearsOwned !== null
      ? `${data.yearsOwned} years`
      : null;
  const saleDate = data.lastSaleDate
    ? new Date(data.lastSaleDate).toLocaleDateString()
    : null;
  const equityPctText =
    data.equityPercent !== null
      ? `${data.equityPercent}%${data.highEquity ? ' · HIGH' : ''}`
      : null;
  const neighborhood = data.neighborhood
    ? [data.neighborhood, data.neighborhoodType].filter(Boolean).join(' · ')
    : null;
  const floodZoneDisplay = data.floodZone
    ? [data.floodZone, data.floodZoneType].filter(Boolean).join(' · ')
    : null;
  const propertyUseDisplay = data.propertyUse
    ? [data.propertyUse, data.propertyUseCode].filter(Boolean).join(' · ')
    : null;
  const documentTypeDisplay = data.documentType
    ? [data.documentType, data.documentTypeCode].filter(Boolean).join(' · ')
    : null;

  const hasOwnership = !!(
    ownerName ||
    data.ownerName2 ||
    data.ownerType ||
    yearsOwnedDisplay ||
    neighborhood ||
    data.corporateOwned !== null ||
    data.absenteeOwner !== null
  );
  const hasFinancials = !!(
    data.lastSaleAmount ||
    saleDate ||
    data.estimatedEquity ||
    equityPctText ||
    data.annualTaxes ||
    data.taxTotal ||
    data.assessedValue ||
    data.improvementValuePercent != null
  );
  const hasProfile = !!(
    data.propertyType ||
    floodZoneDisplay ||
    data.pricePerSquareFoot ||
    data.parcelId ||
    data.apn ||
    propertyUseDisplay ||
    data.landUse ||
    data.county
  );
  const hasAmenities = !!(
    data.roomsCount != null ||
    data.stories != null ||
    data.unitsCount != null ||
    data.basement !== null ||
    data.garage !== null ||
    data.pool !== null ||
    data.deck !== null ||
    data.patio !== null ||
    data.airConditioningAvailable !== null ||
    data.hoa !== null ||
    data.roofConstruction ||
    data.roofMaterial ||
    data.mfh2to4 !== null ||
    data.mfh5plus !== null
  );
  const hasMortgage = !!(
    data.lenderName ||
    data.openMortgageBalance ||
    data.openMortgagePercent != null ||
    data.firstMortgagePercent != null ||
    data.secondMortgagePercent != null ||
    data.thirdMortgagePercent != null ||
    data.lastMortgage1Amount ||
    data.estimatedMortgagePayment ||
    data.adjustableRate !== null ||
    data.assumable !== null ||
    data.privateLender !== null
  );
  // Distress = financial / legal trouble with the property only. Estate
  // events (death on record, inherited) are real seller signals but they're
  // not distress — they surface as neutral pills in the status row instead.
  const hasDistress = !!(
    data.foreclosureStatus ||
    data.foreclosureFilingDate ||
    data.foreclosureAuctionDate ||
    data.foreclosureAmount ||
    data.preForeclosure === true ||
    data.reo === true ||
    data.auction === true ||
    data.auctionDate ||
    data.noticeType ||
    data.taxLien === true ||
    data.judgment === true ||
    // Also surface the "all-clear" view when any of these flags came back as
    // an explicit false — gives the user reassurance that data was checked.
    data.preForeclosure === false ||
    data.reo === false ||
    data.auction === false ||
    data.taxLien === false ||
    data.judgment === false
  );
  const hasListing = !!(
    data.mlsStatus ||
    data.mlsListingPrice ||
    data.mlsListingDate ||
    data.mlsDaysOnMarket != null ||
    data.listingAmount ||
    data.forSale !== null ||
    data.priceReduced !== null ||
    data.mlsLastSaleDate ||
    data.mlsLastStatusDate
  );
  const hasPriorOwnership = !!(
    data.priorOwner ||
    data.priorOwnerAddress ||
    data.priorOwnerMonthsOwned != null ||
    data.priorSaleAmount ||
    data.priorSaleDate ||
    data.latestArmsLengthSaleDate ||
    data.latestNonArmsLengthSaleDate
  );
  const hasSourceMeta = !!(
    data.lastUpdateDate ||
    data.recordingDate ||
    documentTypeDisplay ||
    data.propertyId
  );
  const hasIncome = !!(data.rentAmount || data.medianIncome);
  const hasDemographics = !!(
    data.population != null ||
    data.populationDensity != null ||
    data.populationGrowth != null ||
    data.medianHouseholdIncome ||
    data.averageHouseholdSize != null ||
    data.totalHouseholds != null ||
    data.eduHighSchool != null ||
    data.eduCollege != null ||
    data.eduBachelors != null
  );
  const hasRental = !!(
    data.rentalStudio ||
    data.rentalOneBd ||
    data.rentalTwoBd ||
    data.rentalThreeBd
  );

  return (
    <div className="flex flex-col gap-5 pb-4 pr-2">
      {/* Hero card — gradient banner with embedded mini stats */}
      <div className="relative rounded-2xl border border-foreground/15 overflow-hidden shadow-md bg-card">
        <div
          className="relative px-5 py-6 sm:py-7 bg-gradient-to-r from-amber-500 to-orange-600 dark:from-amber-300 dark:to-orange-400"
        >
          {/* White radial highlights for depth — kept above the linear gradient via separate layer */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage:
                'radial-gradient(circle at 20% 30%, rgba(255,255,255,0.18) 0, transparent 40%), radial-gradient(circle at 85% 70%, rgba(255,255,255,0.12) 0, transparent 50%)',
            }}
          />
          {/* Subtle dot pattern overlay */}
          <div
            className="absolute inset-0 opacity-[0.08] pointer-events-none"
            style={{
              backgroundImage:
                'radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)',
              backgroundSize: '16px 16px',
            }}
          />
          <div className="relative flex flex-col gap-3 text-white dark:text-zinc-900">
            {/* On mobile: address stacks on top, estimated value drops below.
                On sm+: address left, value right-aligned. */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[10px] font-bold tracking-[0.15em] text-white dark:text-zinc-900 uppercase mb-1 drop-shadow-sm">
                  Current home on file
                </div>
                <h3 className="text-sm sm:text-base xl:text-xl font-bold leading-tight drop-shadow-sm break-words">
                  {data.address}
                </h3>
                {typeof data.lat === 'number' && typeof data.lng === 'number' && (
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${data.lat},${data.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="no-link-style inline-flex items-center gap-1 text-[11px] text-white hover:text-white/80 dark:text-zinc-900 dark:hover:text-zinc-900/80 transition-colors mt-1"
                  >
                    <MapPin className="w-3 h-3" />
                    View on map
                  </a>
                )}
              </div>
              {data.estValue && (
                <div className="text-left sm:text-right flex-shrink-0">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-white dark:text-zinc-900 drop-shadow-sm">
                    Estimated value
                  </div>
                  <div className="text-xl sm:text-2xl xl:text-3xl font-extrabold tabular-nums drop-shadow-sm">
                    {data.estValue}
                  </div>
                  {data.valueChange && data.valueChangePct !== null && (() => {
                    const pct = data.valueChangePct;
                    const isUp = pct >= 0;
                    return (
                      <div className="inline-flex items-center gap-1 mt-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-white/25 backdrop-blur-sm text-white dark:bg-zinc-900/15 dark:text-zinc-900">
                        {isUp ? (
                          <ArrowUpRight className="w-3 h-3" />
                        ) : (
                          <ArrowDownRight className="w-3 h-3" />
                        )}
                        {data.valueChange} since purchase
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Hero meta strip — mini-stats below the gradient banner. On mobile
            the row scrolls horizontally so all five facts stay visible without
            wrapping into uneven rows; on sm+ it's a regular 5-col grid. */}
        {(data.beds !== null ||
          data.baths !== null ||
          data.sqft !== null ||
          data.yearBuilt ||
          data.lotSqft) && (
          <div className="flex sm:grid sm:grid-cols-5 overflow-x-auto sm:overflow-visible divide-x divide-foreground/10 border-t border-foreground/10 bg-card scrollbar-custom">
            <MetaStat
              label="Beds"
              value={data.beds !== null ? String(data.beds) : null}
            />
            <MetaStat
              label="Baths"
              value={data.baths !== null ? String(data.baths) : null}
            />
            <MetaStat
              label="Interior"
              value={data.sqft ? `${data.sqft.toLocaleString()} sf` : null}
            />
            <MetaStat
              label="Lot"
              value={data.lotSqft ? `${data.lotSqft.toLocaleString()} sf` : null}
            />
            <MetaStat
              label="Built"
              value={data.yearBuilt ? String(data.yearBuilt) : null}
            />
          </div>
        )}
      </div>

      {/* Status pills — fast read of the meaningful flags */}
      {pills.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {pills.map((p, i) => (
            <span
              key={i}
              className={cn(
                'inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full ring-1 shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5',
                PILL_TONES[p.tone],
              )}
            >
              {p.icon}
              {p.label}
            </span>
          ))}
        </div>
      )}

      {/* KPI strip — the four most actionable numbers, using the shared
          .remi-card-gradient CSS family for the tinted border + glow effect. */}
      {kpis.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {kpis.slice(0, 4).map((k, i) => {
            const tone = k.tone ?? 'gray';
            // PillTone → .remi-card-gradient tone modifier. Same colour families,
            // one source of truth for the gradient + border + glow look.
            const cssTone = (
              {
                green: 'emerald',
                amber: 'amber',
                red: 'rose',
                blue: 'sky',
                gray: 'slate',
                purple: 'violet',
              } as const
            )[tone];
            // One stand-alone class per card so twMerge can't collapse it.
            const gradientClass =
              cssTone === 'amber'
                ? 'remi-card-gradient'
                : `remi-card-gradient-${cssTone}`;
            return (
              <div
                key={i}
                className={`group ${gradientClass} p-3.5 flex flex-col gap-1 transition-all hover:-translate-y-0.5`}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={cn(
                      'inline-flex items-center justify-center w-7 h-7 rounded-lg ring-1',
                      PILL_TONES[tone],
                    )}
                  >
                    {k.icon}
                  </span>
                  <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                    {k.label}
                  </span>
                </div>
                <div className="text-2xl font-extrabold text-foreground tabular-nums leading-tight truncate mt-1">
                  {k.value}
                </div>
                {k.sub && (
                  <div className="text-[11px] text-muted-foreground/90 truncate">{k.sub}</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Section: Owner */}
      {(hasOwnership || hasPriorOwnership) && (
        <Section title="Owner" icon={User}>
          {hasOwnership && (
            <Block title="Current owner">
              <Field label="Owner of record" value={ownerName} />
              <Field label="Co-owner" value={data.ownerName2} />
              <Field label="Owner type" value={data.ownerType} />
              <Field label="Corporate-owned" value={boolIcon(data.corporateOwned)} />
              <Field label="Absentee owner" value={boolIcon(data.absenteeOwner)} />
              <Field label="In-state absentee" value={boolIcon(data.inStateAbsenteeOwner)} />
              <Field label="Out-of-state absentee" value={boolIcon(data.outOfStateAbsenteeOwner)} />
              <Field label="Years owned" value={yearsOwnedDisplay} />
              <Field label="Neighborhood" value={neighborhood} />
            </Block>
          )}
          {hasPriorOwnership && (
            <Block title="Prior ownership">
              <Field label="Prior owner" value={data.priorOwner} />
              <Field label="Prior owner address" value={data.priorOwnerAddress} />
              <Field label="Prior owner individual" value={boolIcon(data.priorOwnerIndividual)} />
              <Field
                label="Prior owner months owned"
                value={data.priorOwnerMonthsOwned != null ? String(data.priorOwnerMonthsOwned) : null}
              />
              <Field label="Prior sale amount" value={data.priorSaleAmount} />
              <Field label="Prior sale date" value={data.priorSaleDate} />
              <Field label="Prior arms-length sale" value={data.priorArmsLengthSaleAmount} />
              <Field label="Prior arms-length date" value={data.priorArmsLengthSaleDate} />
              <Field label="Latest arms-length sale" value={data.latestArmsLengthSaleAmount} />
              <Field label="Latest arms-length date" value={data.latestArmsLengthSaleDate} />
              <Field label="Latest non-arms-length" value={data.latestNonArmsLengthSaleDate} />
            </Block>
          )}
        </Section>
      )}

      {/* Section: The home */}
      {(hasProfile || hasAmenities) && (
        <Section title="The home" icon={Building2}>
          {hasProfile && (
            <Block title="Profile">
              <Field label="Property type" value={data.propertyType} />
              <Field label="Property use" value={propertyUseDisplay} />
              <Field label="Land use" value={data.landUse} />
              <Field label="Flood zone" value={floodZoneDisplay} />
              <Field label="Price / sq ft" value={data.pricePerSquareFoot} />
              <Field label="Parcel #" value={data.parcelId} />
              <Field label="APN" value={data.apn !== data.parcelId ? data.apn : null} />
              <Field label="County" value={data.county} />
              <Field label="FIPS" value={data.fips} />
            </Block>
          )}
          {hasAmenities && (
            <Block title="Building features">
              <Field
                label="Total rooms"
                value={data.roomsCount != null ? String(data.roomsCount) : null}
              />
              <Field
                label="Stories"
                value={data.stories != null ? String(data.stories) : null}
              />
              <Field
                label="Units"
                value={data.unitsCount != null ? String(data.unitsCount) : null}
              />
              <Field label="Multi-family 2–4 units" value={boolIcon(data.mfh2to4)} />
              <Field label="Multi-family 5+ units" value={boolIcon(data.mfh5plus)} />
              <Field label="Garage" value={boolIcon(data.garage)} />
              <Field label="Basement" value={boolIcon(data.basement)} />
              <Field label="Pool" value={boolIconWithArea(data.pool, data.poolArea)} />
              <Field label="Deck" value={boolIconWithArea(data.deck, data.deckArea)} />
              <Field label="Patio" value={boolIconWithArea(data.patio, data.patioArea)} />
              <Field label="A/C" value={boolIcon(data.airConditioningAvailable)} />
              <Field label="HOA" value={boolIcon(data.hoa)} />
              <Field label="Roof construction" value={data.roofConstruction} />
              <Field label="Roof material" value={data.roofMaterial} />
            </Block>
          )}
        </Section>
      )}

      {/* Section: Money */}
      {(hasFinancials || hasMortgage || hasIncome) && (
        <Section title="Money" icon={DollarSign}>
          {hasFinancials && (
            <Block title="Purchase & equity">
              <Field label="Last sale" value={data.lastSaleAmount} />
              <Field label="Sale date" value={saleDate} />
              <Field label="Arms-length" value={boolIcon(data.lastSaleArmsLength)} />
              <Field label="Estimated equity" value={data.estimatedEquity} />
              <Field label="Equity %" value={equityPctText} />
              <Field label="Negative equity" value={boolIcon(data.negativeEquity)} />
              <Field label="Free & clear" value={boolIcon(data.freeClear)} />
              <Field label="Tax assessed" value={data.taxTotal} />
              <Field label="Improvement value" value={data.taxImprovement} />
              <Field label="Land value" value={data.taxLand} />
              <Field
                label="Improvement %"
                value={data.improvementValuePercent != null ? `${data.improvementValuePercent}%` : null}
              />
              <Field label="Assessed value" value={data.assessedValue} />
              <Field label="Assessed improvement" value={data.assessedImprovementValue} />
              <Field label="Assessed land" value={data.assessedLandValue} />
              <Field label="Tax year" value={data.taxYear != null ? String(data.taxYear) : null} />
              <Field label="Annual taxes" value={data.annualTaxes} />
            </Block>
          )}
          {hasMortgage && (
            <Block title="Mortgage">
              <Field label="Lender" value={data.lenderName} />
              <Field label="Last loan amount" value={data.lastMortgage1Amount} />
              <Field label="Open balance" value={data.openMortgageBalance} />
              <Field
                label="Open mortgage %"
                value={data.openMortgagePercent != null ? `${data.openMortgagePercent}%` : null}
              />
              <Field
                label="1st mortgage %"
                value={data.firstMortgagePercent != null ? `${data.firstMortgagePercent}%` : null}
              />
              <Field
                label="2nd mortgage %"
                value={data.secondMortgagePercent != null ? `${data.secondMortgagePercent}%` : null}
              />
              <Field
                label="3rd mortgage %"
                value={data.thirdMortgagePercent != null ? `${data.thirdMortgagePercent}%` : null}
              />
              <Field label="Est. monthly payment" value={data.estimatedMortgagePayment} />
              <Field label="Adjustable rate" value={boolIcon(data.adjustableRate)} />
              <Field label="Assumable" value={boolIcon(data.assumable)} />
              <Field label="Private lender" value={boolIcon(data.privateLender)} />
            </Block>
          )}
          {hasIncome && (
            <Block title="Income & rent">
              <Field label="Rent estimate" value={data.rentAmount} />
              <Field label="Median area income" value={data.medianIncome} />
            </Block>
          )}
        </Section>
      )}

      {/* Section: Market activity */}
      {(hasListing || data.salesHistory.length > 0 || data.mortgages.length > 0) && (
        <Section title="Market activity" icon={TrendingUp}>
          {hasListing && (
            <Block title="MLS & listing">
              <Field label="MLS status" value={data.mlsStatus} />
              <Field label="For sale" value={boolIcon(data.forSale)} />
              <Field label="Price reduced" value={boolIcon(data.priceReduced)} />
              <Field label="Listing price" value={data.listingAmount} />
              <Field
                label="Listing : value"
                value={
                  data.listingPriceToValuePercent != null
                    ? `${data.listingPriceToValuePercent}%`
                    : null
                }
              />
              <Field label="MLS list price" value={data.mlsListingPrice} />
              <Field label="MLS listed" value={data.mlsListingDate} />
              <Field
                label="Days on market"
                value={data.mlsDaysOnMarket != null ? String(data.mlsDaysOnMarket) : null}
              />
              <Field label="Active" value={boolIcon(data.mlsActive)} />
              <Field label="Pending" value={boolIcon(data.mlsPending)} />
              <Field label="Sold" value={boolIcon(data.mlsSold)} />
              <Field label="Cancelled" value={boolIcon(data.mlsCancelled)} />
              <Field label="Failed" value={boolIcon(data.mlsFailed)} />
              <Field label="Has photos" value={boolIcon(data.mlsHasPhotos)} />
              <Field label="Last MLS sale" value={data.mlsLastSaleDate} />
              <Field label="Last status change" value={data.mlsLastStatusDate} />
            </Block>
          )}
          {data.salesHistory.length > 0 && (
            <Table
              title="Sales history"
              columns={['Date', 'Price', 'Document']}
              rows={data.salesHistory.map((sale) => [
                sale.date ? new Date(sale.date).toLocaleDateString() : '—',
                (sale.price ?? sale.amount) ? formatCurrency((sale.price ?? sale.amount) as number) : '—',
                sale.documentType ?? '—',
              ])}
            />
          )}
          {data.mortgages.length > 0 && (
            <Table
              title="Mortgage history"
              columns={['Lender', 'Amount', 'Date', 'Type', 'Rate']}
              rows={data.mortgages.map((m, i) => {
                const amount = m.amount ?? m.loanAmount;
                const date = m.recordDate ?? m.date;
                const rate = m.interestRate ?? m.rate;
                return [
                  m.lender || `Loan ${i + 1}`,
                  amount ? formatCurrency(amount) : '—',
                  date ? new Date(date).toLocaleDateString() : '—',
                  (m.loanType ?? m.type) ?? '—',
                  rate != null ? `${rate}%` : '—',
                ];
              })}
            />
          )}
        </Section>
      )}

      {/* Section: Area */}
      {(hasDemographics || hasRental || data.schools.length > 0) && (
        <Section title="Area" icon={Globe}>
          {hasDemographics && (
            <Block title="Demographics">
              <Field
                label="Population"
                value={data.population != null ? data.population.toLocaleString() : null}
              />
              <Field
                label="Density"
                value={
                  data.populationDensity != null
                    ? `${data.populationDensity.toLocaleString()} /sq mi`
                    : null
                }
              />
              <Field
                label="Growth"
                value={data.populationGrowth != null ? `${data.populationGrowth}%` : null}
              />
              <Field label="Median income" value={data.medianHouseholdIncome} />
              <Field
                label="Avg household size"
                value={data.averageHouseholdSize != null ? String(data.averageHouseholdSize) : null}
              />
              <Field
                label="Total households"
                value={data.totalHouseholds != null ? data.totalHouseholds.toLocaleString() : null}
              />
              <Field
                label="High-school grads"
                value={data.eduHighSchool != null ? `${data.eduHighSchool}%` : null}
              />
              <Field
                label="College grads"
                value={data.eduCollege != null ? `${data.eduCollege}%` : null}
              />
              <Field
                label="Bachelor's degree"
                value={data.eduBachelors != null ? `${data.eduBachelors}%` : null}
              />
            </Block>
          )}
          {hasRental && (
            <Block title="Rental estimates">
              <Field label="Studio" value={data.rentalStudio} />
              <Field label="1-bedroom" value={data.rentalOneBd} />
              <Field label="2-bedroom" value={data.rentalTwoBd} />
              <Field label="3-bedroom" value={data.rentalThreeBd} />
            </Block>
          )}
          {data.schools.length > 0 && (
            <Table
              title="Schools"
              icon={<School className="w-3.5 h-3.5" />}
              columns={['School', 'Level', 'Rating', 'District']}
              rows={data.schools.map((s, i) => [
                s.name || `School ${i + 1}`,
                [s.level ?? s.type, s.grades].filter(Boolean).join(' · ') || '—',
                (s.rating ?? s.score) != null ? `${s.rating ?? s.score}/10` : '—',
                s.district ?? '—',
              ])}
            />
          )}
        </Section>
      )}

      {/* Section: Source & recording — quiet metadata at the bottom */}
      {hasSourceMeta && (
        <Section title="Source & recording" icon={ScrollText}>
          <Block title="Document">
            <Field label="Document type" value={documentTypeDisplay} />
            <Field label="Recording date" value={data.recordingDate} />
            <Field label="Last data update" value={data.lastUpdateDate} />
            <Field label="Property ID" value={data.propertyId} />
          </Block>
        </Section>
      )}

      {/* Distress callout — switches between a red "X active" alert layout
          and a green "all clear" reassurance based on the actual flags.
          Pinned near the bottom (just above Selling signals + Data sources)
          so the rest of the page reads first. */}
      {hasDistress && (() => {
        const flags: { label: string; active: boolean | null; detail?: string | null }[] = [
          {
            label: 'Foreclosure',
            active:
              data.foreclosureStatus !== null &&
              !/^(not|no|none|clear)/i.test(data.foreclosureStatus.trim()),
            detail: data.foreclosureStatus,
          },
          {
            label: 'Pre-foreclosure',
            active: data.preForeclosure,
            detail: data.foreclosureFilingDate
              ? `Filed ${new Date(data.foreclosureFilingDate).toLocaleDateString()}`
              : null,
          },
          { label: 'REO', active: data.reo },
          {
            label: 'Auction',
            active: data.auction,
            detail: data.auctionDate ?? data.foreclosureAuctionDate ?? data.noticeType,
          },
          {
            label: 'Debt',
            active: !!data.foreclosureAmount,
            detail: data.foreclosureAmount,
          },
          { label: 'Tax lien', active: data.taxLien },
          { label: 'Judgment', active: data.judgment },
        ];
        const active = flags.filter((f) => f.active === true);
        const cleared = flags.filter((f) => f.active === false);
        const hasActive = active.length > 0;
        return (
          <div
            className={cn(
              'relative p-4 overflow-hidden',
              hasActive ? 'remi-card-gradient-rose' : 'remi-card-gradient-emerald',
            )}
          >
            <div
              className="absolute -top-16 -right-16 w-40 h-40 rounded-full opacity-30 pointer-events-none"
              style={{
                background: hasActive
                  ? 'radial-gradient(circle, rgba(239, 68, 68, 0.4) 0%, transparent 70%)'
                  : 'radial-gradient(circle, rgba(16, 185, 129, 0.35) 0%, transparent 70%)',
              }}
            />
            <div className="relative flex items-center gap-2 mb-3">
              <div
                className={cn(
                  'rounded-md p-1.5 shrink-0 flex items-center justify-center',
                  hasActive
                    ? 'bg-gradient-to-r from-red-500 to-rose-600 dark:from-red-400 dark:to-rose-500'
                    : 'bg-gradient-to-r from-emerald-500 to-green-600 dark:from-emerald-400 dark:to-green-500',
                )}
              >
                {hasActive ? (
                  <AlertTriangle className="w-3.5 h-3.5 text-white dark:text-zinc-900" />
                ) : (
                  <CheckCircle2 className="w-3.5 h-3.5 text-white dark:text-zinc-900" />
                )}
              </div>
              <div className="flex flex-col leading-tight">
                <span
                  className={cn(
                    'text-[12px] font-extrabold uppercase tracking-[0.14em]',
                    hasActive
                      ? 'text-red-700 dark:text-red-200'
                      : 'text-emerald-700 dark:text-emerald-200',
                  )}
                >
                  Distress signals
                </span>
                <span className="text-[11px] font-medium text-foreground/70 mt-0.5">
                  {active.length} active · {cleared.length} clear
                </span>
              </div>
            </div>

            {hasActive && (
              <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                {active.map((f, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2 px-3 py-2 rounded-lg bg-white/70 dark:bg-red-500/10 ring-1 ring-red-300/60 dark:ring-red-500/30 shadow-sm"
                  >
                    <AlertTriangle className="w-4 h-4 mt-0.5 text-red-600 dark:text-red-300 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-bold text-red-800 dark:text-red-200 leading-tight">
                        {f.label}
                      </div>
                      {f.detail && (
                        <div className="text-[11px] text-red-700/80 dark:text-red-300/80 mt-0.5 truncate">
                          {f.detail}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {cleared.length > 0 && (
              <div className="relative">
                {hasActive && (
                  <div className="text-[10px] font-bold uppercase tracking-wider text-foreground/50 mb-1.5">
                    Other checks passed
                  </div>
                )}
                <div className="flex flex-wrap gap-1.5">
                  {cleared.map((f, i) => (
                    <span
                      key={i}
                      className={cn(
                        'inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ring-1',
                        hasActive
                          ? 'bg-muted/40 text-muted-foreground ring-foreground/10'
                          : 'bg-white/70 dark:bg-emerald-500/10 text-emerald-800 dark:text-emerald-200 ring-emerald-300/60 dark:ring-emerald-500/30',
                      )}
                    >
                      <CheckCircle2 className="w-3 h-3 opacity-70" />
                      {f.label}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Selling signals — same layout as the Distress callout but in green
          since these are POSITIVE seller indicators (long tenure, high equity,
          active shopping, etc.). */}
      {data.signals.length > 0 && (
        <div className="relative remi-card-gradient-emerald p-4 overflow-hidden">
          <div
            className="absolute -top-16 -right-16 w-40 h-40 rounded-full opacity-30 pointer-events-none"
            style={{
              background:
                'radial-gradient(circle, rgba(16, 185, 129, 0.35) 0%, transparent 70%)',
            }}
          />
          <div className="relative flex items-center gap-2 mb-3">
            <div className="btn-ai-gradient rounded-md p-1.5 shrink-0 flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5 text-white dark:text-zinc-900" />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="text-[12px] font-extrabold uppercase tracking-[0.14em] text-emerald-700 dark:text-emerald-200">
                Selling signals
              </span>
              <span className="text-[11px] font-medium text-foreground/70 mt-0.5">
                {data.signals.length} {data.signals.length === 1 ? 'signal' : 'signals'} · suggests
                a sale soon
              </span>
            </div>
          </div>

          <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-2">
            {data.signals.map((sig, i) => (
              <div
                key={i}
                className="px-3 py-2 rounded-lg bg-white/70 dark:bg-emerald-500/10 ring-1 ring-emerald-300/60 dark:ring-emerald-500/30 shadow-sm"
              >
                <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                  <span className="text-sm font-bold text-emerald-800 dark:text-emerald-200 leading-tight">
                    {sig.title}
                  </span>
                  <span
                    className={cn(
                      'inline-flex items-center text-[9px] font-bold tracking-[0.1em] px-1.5 py-0.5 rounded-full ring-1',
                      sig.tier === 'high' &&
                        'bg-emerald-100 text-emerald-700 ring-emerald-300 dark:bg-emerald-500/20 dark:text-emerald-200 dark:ring-emerald-500/40',
                      sig.tier === 'med' &&
                        'bg-amber-100 text-amber-700 ring-amber-300 dark:bg-amber-500/20 dark:text-amber-200 dark:ring-amber-500/40',
                      sig.tier === 'low' &&
                        'bg-muted text-muted-foreground ring-foreground/10',
                    )}
                  >
                    {sig.strength}
                  </span>
                </div>
                <div className="text-[11px] text-emerald-700/80 dark:text-emerald-300/80 leading-snug">
                  {sig.detail}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-lg border border-foreground/20 bg-muted/20 p-3">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
          Data sources
        </div>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex flex-wrap gap-2 text-xs">
            <Source
              name="Remi Property Enrichment"
              live
              when={
                data.lastEnrichmentAt
                  ? `Last updated ${new Date(data.lastEnrichmentAt).toLocaleDateString()}`
                  : 'pending'
              }
            />
          </div>
          {data.confidenceScore !== null &&
            (() => {
              const raw = data.confidenceScore as number;
              const pct = raw <= 1 ? raw * 100 : raw;
              const display = raw <= 1 ? raw.toFixed(2) : String(Math.round(raw));
              const colors =
                pct >= 80
                  ? 'bg-green-100 text-green-700 ring-green-400/60 dark:bg-green-500/15 dark:text-green-300 dark:ring-green-500/40'
                  : pct >= 50
                  ? 'bg-amber-100 text-amber-700 ring-amber-400/60 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-amber-500/40'
                  : 'bg-red-100 text-red-700 ring-red-400/60 dark:bg-red-500/15 dark:text-red-300 dark:ring-red-500/40';
              return (
                <Tooltip
                  content="How confident Remi is in this property data, based on data source completeness and address match quality."
                  position="top"
                >
                  <div className="flex items-center gap-3 cursor-help">
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      Confidence
                    </span>
                    <div
                      className={cn(
                        'inline-flex items-center justify-center min-w-[36px] h-8 px-2 rounded-full ring-2 text-xs font-bold tabular-nums',
                        colors,
                      )}
                    >
                      {display}
                    </div>
                  </div>
                </Tooltip>
              );
            })()}
        </div>
      </div>
    </div>
  );
};

const MetaStat: React.FC<{ label: string; value: string | null }> = ({ label, value }) => (
  // On mobile the parent scrolls horizontally — give each cell a sensible
  // minimum width so the values stay readable instead of cramming.
  <div className="px-3 py-2.5 flex flex-col gap-0.5 min-w-[80px] flex-shrink-0 sm:flex-shrink sm:min-w-0">
    <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
      {label}
    </span>
    <span className="text-sm font-semibold text-foreground tabular-nums truncate">
      {value ?? '—'}
    </span>
  </div>
);

const Section: React.FC<{
  title: string;
  icon?: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}> = ({ title, icon: Icon, children }) => (
  <div className="flex flex-col gap-3">
    <div className="flex items-center gap-1.5">
      {Icon && <IconPill icon={Icon} size="md" />}
      <span className="text-[10px] font-semibold uppercase tracking-wider text-foreground/70">
        {title}
      </span>
      <div className="flex-1 h-px bg-foreground/10 ml-1" />
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
  </div>
);

const Block: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <div className="group rounded-2xl overflow-hidden border border-foreground/15 bg-card shadow-sm transition-all hover:shadow-md hover:border-foreground/25">
    {/* Subtle gradient accent strip at the top of every block */}
    <div className="h-1 bg-gradient-to-r from-amber-400/60 via-orange-400/30 to-transparent" />
    <div className="px-4 pt-3 pb-2">
      <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-foreground/75">
        {title}
      </span>
    </div>
    <div className="divide-y divide-foreground/10">{children}</div>
  </div>
);

const Field: React.FC<{
  label: string;
  value: string | React.ReactNode | null;
  muted?: boolean;
}> = ({ label, value, muted }) => {
  if (value == null || value === '' || value === '—') return null;
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2 xl:py-2.5 info-divider">
      <span className="text-xs xl:text-sm text-muted-foreground flex-shrink-0">
        {label}
      </span>
      <span
        className={cn(
          'text-xs xl:text-sm text-right truncate tabular-nums',
          muted ? 'text-muted-foreground italic' : 'text-foreground',
        )}
      >
        {value}
      </span>
    </div>
  );
};

// Compact table for repeated row data — sales history, mortgages, schools.
// Spans the full row of the parent section grid so wide tables don't get squeezed.
const Table: React.FC<{
  title: string;
  icon?: React.ReactNode;
  columns: string[];
  rows: (string | React.ReactNode)[][];
}> = ({ title, icon, columns, rows }) => (
  <div className="sm:col-span-2 rounded-2xl overflow-hidden border border-foreground/15 bg-card shadow-sm transition-all hover:shadow-md">
    <div className="h-1 bg-gradient-to-r from-amber-400/60 via-orange-400/30 to-transparent" />
    <div className="px-4 pt-3 pb-2 flex items-center gap-1.5">
      {icon && <span className="text-foreground/60">{icon}</span>}
      <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-foreground/75">
        {title}
      </span>
      <span className="text-[10px] font-semibold ml-auto px-2 py-0.5 rounded-full bg-muted/60 text-muted-foreground">
        {rows.length} {rows.length === 1 ? 'entry' : 'entries'}
      </span>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-t border-foreground/10 bg-gradient-to-b from-muted/50 to-muted/20 text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
            {columns.map((c, i) => (
              <th key={i} className="text-left font-bold px-4 py-2.5 whitespace-nowrap">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-foreground/10">
          {rows.map((row, i) => (
            <tr
              key={i}
              className={cn(
                'transition-colors hover:bg-muted/30',
                i % 2 === 1 && 'bg-muted/10',
              )}
            >
              {row.map((cell, j) => (
                <td
                  key={j}
                  className="px-4 py-2.5 text-foreground tabular-nums whitespace-nowrap"
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const Source: React.FC<{
  name: string;
  when?: string;
  live?: boolean;
  muted?: boolean;
}> = ({ name, when, live, muted }) => (
  <span className={cn('inline-flex items-center gap-1.5', muted && 'opacity-60')}>
    <span className="text-foreground">{name}</span>
    {live && <LiveIndicator size="sm" />}
    {when && <span className="text-muted-foreground italic">{when}</span>}
  </span>
);
